import { useState, useEffect, Fragment } from "react";
import Layout from "../../components/Layout/Layout";
import {
  SearchIcon,
  UserGroupIcon,
  MailIcon,
  PhoneIcon,
   OfficeBuildingIcon,
   PlusCircleIcon,
  IdentificationIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  XIcon,
  CheckCircleIcon,
  XCircleIcon
} from "@heroicons/react/outline";
import { Dialog, Switch } from "@headlessui/react";
import ActionDropdown from "../../components/ActionDropdown";
import fire from "./../../utils/Toastr";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import Paginate from "../../utils/Paginate";
import { useSearchParams, useNavigate } from "react-router-dom";
import CreatePayrollOfficerModal from "./CreatePayrollOfficerModal";
import UpdatePayrollOfficerModal from "./UpdatePayrollOfficerModal";


export default function ThirdPartyPayrollOfficer() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Pagination states
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Application filter
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState("");

  const filteredOrganizations = selectedAppId
    ? organizations.filter(org => {
        const app = applications.find(a => a.applicationId === selectedAppId);
        return app ? org.applicationName === app.applicationName : false;
      })
    : [];

  useEffect(() => {
    fetchOrganizations();
    fetchApplications();
  }, []);

  // Check for organization parameter in URL
  useEffect(() => {
    const orgFromUrl = searchParams.get("organization");
    if (orgFromUrl && orgFromUrl !== "undefined") {
      setSelectedOrgId(orgFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedOrgId && selectedOrgId !== "undefined") {
      fetchPayrollOfficers(1);
    }
  }, [selectedOrgId]);

  const fetchOrganizations = async () => {
    try {
      let allOrgs = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await ThirdPartyServices.listOrganizations(page, 100);
        if (response.status && response.data?.result) {
          allOrgs = [...allOrgs, ...response.data.result];
          totalPages = response.data.totalPages || 1;
          page++;
        } else {
          break;
        }
      } while (page <= totalPages);

      setOrganizations(allOrgs);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await ThirdPartyServices.listApplications(1, 100);
      if (response.status && response.data?.result) {
        setApplications(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const fetchPayrollOfficers = async (page = 1) => {
    if (!selectedOrgId || selectedOrgId === "undefined") {
      setError("Please select an organization");
      setOfficers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.listPayrollOfficers(
        selectedOrgId,
        page,
        10,
      );

      if (response.status && response.data?.result) {
        setOfficers(response.data.result);
        setPageIndex(response.data.pageIndex || page);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPreviousPage(response.data.hasPreviousPage || false);
      } else {
        setError(response.message || "Failed to fetch payroll officers");
        setOfficers([]);
      }
    } catch (err) {
      setError("Failed to fetch payroll officers");
      fire("error", "Failed to fetch payroll officers");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim() || !selectedOrgId || selectedOrgId === "undefined") {
      fire("warning", "Please select an organization and enter search term");
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.searchPayrollOfficer(
        selectedOrgId,
        searchTerm,
      );

      if (response.status && response.data?.result) {
        setOfficers(response.data.result);
        setTotalCount(response.data.result.length);
        setTotalPages(1);
        setPageIndex(1);
        if (response.data.result.length === 0) {
          fire("info", "No payroll officers found matching your search");
        }
      } else {
        fire("info", "No payroll officers found");
        setOfficers([]);
        setTotalCount(0);
      }
    } catch (err) {
      fire("error", "Search failed");
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (officer) => {
    if (!selectedOrgId || !officer.tenantId) {
      fire("error", "Missing required organization or tenant information");
      return;
    }

    setStatusUpdating(true);
    try {
      const response = await ThirdPartyServices.updatePayrollOfficerStatus({
        organizationId: selectedOrgId,
        tenantId: officer.tenantId,
        payrollOfficerId: officer.payrollOfficerId,
        modifiedBy: "admin", // You might want to get this from auth context
        isActive: !officer.isActive,
      });

      if (response.status) {
        fire("success", `Payroll officer ${!officer.isActive ? 'activated' : 'deactivated'} successfully`);
        // Update local state
        setOfficers(prevOfficers =>
          prevOfficers.map(o =>
            o.payrollOfficerId === officer.payrollOfficerId
              ? { ...o, isActive: !o.isActive }
              : o
          )
        );
      } else {
        fire("error", response.message || "Failed to update status");
      }
    } catch (err) {
      fire("error", "Failed to update status");
      console.error("Status update error:", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleUpdateSuccess = (updatedOfficer) => {
    setOfficers(prevOfficers =>
      prevOfficers.map(o =>
        o.payrollOfficerId === updatedOfficer.payrollOfficerId
          ? updatedOfficer
          : o
      )
    );
    setShowUpdateModal(false);
    setSelectedOfficer(null);
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    fetchPayrollOfficers(newPage);
  };

  const viewOfficerDetails = (officer) => {
    navigate(
      `/thirdparty/payroll-officers/${selectedOrgId}/${officer.payrollOfficerId}/view`,
    );
  };

  const handleEditOfficer = (officer) => {
    setSelectedOfficer(officer);
    setShowUpdateModal(true);
  };

  const handleCreateSuccess = () => {
    fetchPayrollOfficers(pageIndex);
  };

  return (
    <Layout
      current="thirdparty"
      pageTag="thirdparty"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Payroll Officers</h1>
              <p className="mt-2 text-sm text-gray-700">
                Manage payroll officers who can create and process payrolls
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setShowCreateModal(true)}
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                disabled={!selectedOrgId}
              >
                <PlusCircleIcon className="w-4 mr-1.5" />
                Add Payroll Officer
              </button>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Application Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application
                </label>
                <select
                  value={selectedAppId}
                  onChange={(e) => {
                    setSelectedAppId(e.target.value);
                    setSelectedOrgId("");
                    setSearchTerm("");
                    setPageIndex(1);
                    setOfficers([]);
                  }}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  <option value="">Select Application</option>
                  {applications.map((app) => (
                    <option key={app.applicationId} value={app.applicationId}>
                      {app.applicationName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Organization Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <select
                  value={selectedOrgId}
                  onChange={(e) => {
                    const orgId = e.target.value;
                    setSelectedOrgId(orgId);
                    setSearchTerm("");
                    setPageIndex(1);
                  }}
                  className={`block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md ${!selectedAppId ? "bg-gray-100" : ""}`}
                  disabled={!selectedAppId}
                >
                  <option value="">{selectedAppId ? "Select Organization" : "Select an application first"}</option>
                  {filteredOrganizations.map((org) => (
                    <option key={org.organizationId} value={org.organizationId}>
                      {org.companyName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Organization must be selected to view payroll officers
                </p>
              </div>

              {/* Search Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Officers
                </label>
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-10 block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                        !selectedOrgId ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder={
                        selectedOrgId
                          ? "Search by name, email, phone or BVN..."
                          : "Select organization first"
                      }
                      disabled={!selectedOrgId}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!selectedOrgId || !selectedAppId}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                      selectedOrgId
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-gray-400 cursor-not-allowed"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                  >
                    Search
                  </button>
                </form>
              </div>

              {/* Stats */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex items-center">
                  <UserGroupIcon className="h-8 w-8 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Total Officers</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {totalCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <HollowDotsSpinner color="#4A90E2" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700">{error}</p>
            </div>
          ) : !selectedAppId ? (
            <div className="text-center py-12">
              <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an Application
              </h3>
              <p className="text-gray-500">
                Please select an application and organization to view payroll officers.
              </p>
            </div>
          ) : !selectedOrgId || selectedOrgId === "undefined" ? (
            <div className="text-center py-12">
              <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an Organization
              </h3>
              <p className="text-gray-500">
                Please select an organization to view payroll officers.
              </p>
            </div>
          ) : officers.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No payroll officers found
              </h3>
              <p className="text-gray-500 mb-4">
                There are no payroll officers for the selected organization.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
              >
                <PlusCircleIcon className="w-4 mr-1.5" />
                Add Your First Payroll Officer
              </button>
            </div>
          ) : (
            <>
              {/* Officers Table */}
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Officer Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          BVN
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {officers.map((officer, i) => (
                        <tr key={officer.payrollOfficerId} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                  <UserCircleIcon className="h-6 w-6 text-blue-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {officer.userName || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-500">
                                <MailIcon className="h-4 w-4 mr-2" />
                                {officer.emailAddress}
                              </div>
                              <div className="flex items-center text-sm text-gray-500">
                                <PhoneIcon className="h-4 w-4 mr-2" />
                                {officer.phoneNumber}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <IdentificationIcon className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="text-sm text-gray-900">{officer.bvn}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              officer.officerType === 'Maker' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {officer.officerType}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Switch
                                checked={officer.isActive}
                                onChange={() => handleStatusToggle(officer)}
                                disabled={statusUpdating}
                                className={`${
                                  officer.isActive ? 'bg-green-600' : 'bg-gray-200'
                                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2`}
                              >
                                <span
                                  className={`${
                                    officer.isActive ? 'translate-x-6' : 'translate-x-1'
                                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                />
                              </Switch>
                              <span className="ml-2 text-sm text-gray-500">
                                {officer.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <ActionDropdown
                              items={[
                                { label: "View Details", onClick: () => viewOfficerDetails(officer) },
                                { label: "Edit Officer", onClick: () => handleEditOfficer(officer) },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Paginate
                    nextPage={hasNextPage}
                    pageIndex={pageIndex}
                    totalCount={totalCount}
                    prevPage={hasPreviousPage}
                    totalPages={totalPages}
                    setPageIndex={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Payroll Officer Modal */}
      <CreatePayrollOfficerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        organizationId={selectedOrgId}
        organizations={organizations}
      />

      {/* Update Payroll Officer Modal */}
      <UpdatePayrollOfficerModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedOfficer(null);
        }}
        onSuccess={handleUpdateSuccess}
        officer={selectedOfficer}
        organizationId={selectedOrgId}
      />

      {/* Officer Details Modal */}
      <Dialog
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl w-full rounded-lg bg-white">
            {selectedOfficer && (
              <>
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Payroll Officer Details
                    </Dialog.Title>
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">
                        Officer Information
                      </h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-xs text-gray-500">Officer ID</dt>
                          <dd className="mt-1 text-sm text-gray-900 font-mono">
                            {selectedOfficer.payrollOfficerId}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Username</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedOfficer.userName || "Not set"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Officer Type</dt>
                          <dd className="mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              selectedOfficer.officerType === 'Maker' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {selectedOfficer.officerType}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">BVN</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedOfficer.bvn}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Contact Information */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">
                        Contact Information
                      </h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-xs text-gray-500">Email Address</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedOfficer.emailAddress}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Phone Number</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedOfficer.phoneNumber}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Account Status */}
                    <div className="md:col-span-2">
                      <h3 className="text-sm font-medium text-gray-500 mb-4">
                        Account Status
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center">
                          {selectedOfficer.isActive ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                              <span className="text-sm text-green-700">Active</span>
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                              <span className="text-sm text-red-700">Inactive</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center">
                          {selectedOfficer.isLocked ? (
                            <>
                              <ShieldCheckIcon className="h-5 w-5 text-red-500 mr-2" />
                              <span className="text-sm text-red-700">Locked</span>
                            </>
                          ) : (
                            <>
                              <ShieldCheckIcon className="h-5 w-5 text-green-500 mr-2" />
                              <span className="text-sm text-green-700">Unlocked</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div className="md:col-span-2 border-t border-gray-200 pt-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-4">
                        System Information
                      </h3>
                      <dl className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-xs text-gray-500">Password Retries</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedOfficer.passwordRetries || 0}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleEditOfficer(selectedOfficer);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Edit Officer
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetailsModal(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </Layout>
  );
}